import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { DataTable } from '../common/DataTable';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SelectGroup,
    SelectLabel,
} from '../ui/select';
import { templateApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FileText, Palette, Download, Upload, Image as ImageIcon, Copy } from 'lucide-react';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");
const SCALE = 2.5;
const FORMATS = {
    'a4-portrait':  { w: 210, h: 297 },
    'a4-landscape': { w: 297, h: 210 },
};

// Template types, grouped by usage category
const TYPE_DEFS = {
    license:         { label: 'Lizenz',                 category: 'bescheinigung' },
    certificate:     { label: 'Lehrgangsbescheinigung', category: 'bescheinigung' },
    payment_receipt: { label: 'Zahlungsbestätigung',    category: 'beleg' },
    in_house_invoice: { label: 'In-House-Rechnung',     category: 'beleg' },
    // legacy (nicht mehr wählbar, nur Anzeige für bestehende Einträge)
    invoice:         { label: 'Rechnung (Legacy)',      category: 'beleg' },
};
const CATEGORIES = { bescheinigung: 'Bescheinigungen', beleg: 'Belege' };
const typeLabel = (type) => TYPE_DEFS[type]?.label || type;
const typeCategory = (type) => TYPE_DEFS[type]?.category || 'bescheinigung';

const emptyForm = {
    name: '',
    type: 'license',
    content: '{}',
    is_active: true,
    orientation: 'a4-portrait',
};

export function TemplatesPage() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const importInputRef = useRef(null);
    const bgFileRef = useRef(null);
    const [bgFile, setBgFile] = useState(null);
    const [bgPreview, setBgPreview] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('all');

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            const response = await templateApi.getAll();
            setTemplates(response.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Vorlagen');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (template = null) => {
        if (template) {
            setSelectedTemplate(template);
            setFormData({
                name: template.name || '',
                type: template.type || 'license',
                content: template.content || '{}',
                is_active: template.is_active !== false,
                orientation: template.orientation || 'a4-portrait',
            });
        } else {
            setSelectedTemplate(null);
            setFormData(emptyForm);
        }
        setBgFile(null);
        setBgPreview(null);
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) { toast.error('Name ist erforderlich'); return; }
        setSaving(true);
        try {
            if (selectedTemplate) {
                await templateApi.update(selectedTemplate.id, formData);
                toast.success('Vorlage aktualisiert');
            } else {
                // Build initial content with background if provided
                let initialContent = '{}';
                const fmt = FORMATS[formData.orientation] || FORMATS['a4-portrait'];
                const canvasW = Math.round(fmt.w * SCALE);
                const canvasH = Math.round(fmt.h * SCALE);

                if (bgFile) {
                    // Upload background image first
                    const uploadRes = await templateApi.uploadAsset(bgFile);
                    const bgUrl = `${BACKEND_URL}${uploadRes.data.url}`;

                    // Load image to get dimensions for scaling
                    const img = await new Promise((resolve, reject) => {
                        const i = new window.Image();
                        i.crossOrigin = 'anonymous';
                        i.onload = () => resolve(i);
                        i.onerror = reject;
                        i.src = bgUrl;
                    });

                    const scaleX = canvasW / img.naturalWidth;
                    const scaleY = canvasH / img.naturalHeight;

                    const bgObject = {
                        type: 'Image',
                        left: canvasW / 2,
                        top: canvasH / 2,
                        originX: 'center',
                        originY: 'center',
                        scaleX,
                        scaleY,
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                        src: bgUrl,
                        selectable: false,
                        evented: false,
                        hasControls: false,
                        hasBorders: false,
                        lockMovementX: true,
                        lockMovementY: true,
                        isBackground: true,
                    };

                    initialContent = JSON.stringify({
                        pages: [{
                            version: '6.4.2',
                            objects: [bgObject],
                        }],
                        format: formData.orientation,
                    });
                } else {
                    initialContent = JSON.stringify({
                        pages: [{ version: '6.4.2', objects: [] }],
                        format: formData.orientation,
                    });
                }

                const res = await templateApi.create({
                    ...formData,
                    content: initialContent,
                    orientation: formData.orientation?.includes('landscape') ? 'landscape' : 'portrait',
                    page_width: fmt.w,
                    page_height: fmt.h,
                });
                toast.success(bgFile ? 'Vorlage mit Hintergrund erstellt' : 'Vorlage erstellt');
                setDialogOpen(false);
                loadTemplates();
                navigate(`/admin/settings/templates/${res.data.id}/edit`);
                return;
            }
            setDialogOpen(false);
            loadTemplates();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await templateApi.delete(selectedTemplate.id);
            toast.success('Vorlage gelöscht');
            setDeleteDialogOpen(false);
            setSelectedTemplate(null);
            loadTemplates();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleExport = async (tmpl) => {
        try {
            const res = await templateApi.exportTemplate(tmpl.id);
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${tmpl.name}.bgpr-template.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Vorlage exportiert');
        } catch {
            toast.error('Fehler beim Exportieren');
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            await templateApi.importTemplate(file);
            toast.success('Vorlage importiert');
            loadTemplates();
        } catch {
            toast.error('Fehler beim Importieren');
        }
        e.target.value = '';
    };

    const handleDuplicate = async (row) => {
        const suggested = `${row.name} (Kopie)`;
        const newName = window.prompt('Name für die Kopie:', suggested);
        if (newName === null) return;
        try {
            await templateApi.duplicate(row.id, newName.trim() || suggested);
            toast.success('Vorlage kopiert');
            loadTemplates();
        } catch {
            toast.error('Fehler beim Kopieren');
        }
    };

    const getTypeLabel = typeLabel;

    const columns = [
        {
            header: 'Name',
            cell: (row) => (
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{row.name}</span>
                </div>
            ),
        },
        {
            header: 'Kategorie',
            cell: (row) => {
                const cat = typeCategory(row.type);
                return (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${cat === 'bescheinigung' ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'}`}>
                        {CATEGORIES[cat] || cat}
                    </span>
                );
            },
        },
        {
            header: 'Typ',
            cell: (row) => (
                <span className="px-2 py-1 text-xs font-medium bg-muted rounded">
                    {getTypeLabel(row.type)}
                </span>
            ),
        },
        {
            header: 'Platzhalter',
            cell: (row) => {
                const placeholders = row.placeholders || [];
                return placeholders.length > 0 ? (
                    <span className="text-xs text-muted-foreground">{placeholders.length} definiert</span>
                ) : (
                    <span className="text-xs text-muted-foreground">Keine</span>
                );
            },
        },
        {
            header: 'Status',
            cell: (row) => (
                <span className={`text-sm ${row.is_active ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {row.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
            ),
        },
        {
            header: 'Aktionen',
            cell: (row) => (
                <div className="flex items-center gap-1">
                    <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/settings/templates/${row.id}/edit`); }}
                        data-testid={`design-template-${row.id}`}
                    >
                        <Palette className="h-3.5 w-3.5 mr-1" /> Gestalten
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(row); }}
                        title="Vorlage kopieren"
                        data-testid={`duplicate-template-${row.id}`}
                    >
                        <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleExport(row); }}
                        title="Vorlage exportieren"
                        data-testid={`export-template-${row.id}`}
                    >
                        <Download className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleOpenDialog(row); }}
                        data-testid={`edit-template-${row.id}`}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTemplate(row);
                            setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-template-${row.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <AdminLayout>
            <PageHeader
                title="PDF-Vorlagen"
                description="Vorlagen für Bescheinigungen (Lizenz, Lehrgangsbescheinigung) und Belege (Zahlungsbestätigung)"
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => importInputRef.current?.click()} data-testid="import-template-button">
                            <Upload className="mr-2 h-4 w-4" />
                            Importieren
                        </Button>
                        <input ref={importInputRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
                        <Button onClick={() => handleOpenDialog()} data-testid="add-template-button">
                            <Plus className="mr-2 h-4 w-4" />
                            Neue Vorlage
                        </Button>
                    </div>
                }
            />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                    <strong>Schnellstart:</strong> Erstellen Sie eine neue Vorlage und laden Sie direkt Ihr bestehendes Design als Hintergrundbild (PNG/JPG) hoch.
                    Der Editor öffnet sich mit dem Hintergrund – Sie platzieren nur noch die Textfelder mit Platzhaltern darüber.
                </p>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <>
                    {/* Category filter tabs */}
                    <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 mb-4 w-fit">
                        {[
                            { v: 'all', l: `Alle (${templates.length})` },
                            { v: 'bescheinigung', l: `Bescheinigungen (${templates.filter(t => typeCategory(t.type) === 'bescheinigung').length})` },
                            { v: 'beleg', l: `Belege (${templates.filter(t => typeCategory(t.type) === 'beleg').length})` },
                        ].map(t => (
                            <button
                                key={t.v}
                                onClick={() => setCategoryFilter(t.v)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${categoryFilter === t.v ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                data-testid={`template-category-${t.v}`}
                            >
                                {t.l}
                            </button>
                        ))}
                    </div>
                    <DataTable
                        columns={columns}
                        data={categoryFilter === 'all' ? templates : templates.filter(t => typeCategory(t.type) === categoryFilter)}
                        emptyMessage="Noch keine Vorlagen erstellt"
                    />
                </>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{selectedTemplate ? 'Vorlage umbenennen' : 'Neue Vorlage erstellen'}</DialogTitle>
                        <DialogDescription>
                            {selectedTemplate ? 'Ändern Sie den Namen oder Typ der Vorlage.' : 'Geben Sie einen Namen und Typ für die neue Vorlage an.'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    placeholder="z.B. Herzgruppenleiter-Lizenz"
                                    data-testid="template-name-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Typ *</Label>
                                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                                        <SelectTrigger data-testid="template-type-select">
                                            <SelectValue placeholder="Typ auswählen" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectLabel>Bescheinigungen</SelectLabel>
                                                <SelectItem value="license">Lizenz</SelectItem>
                                                <SelectItem value="certificate">Lehrgangsbescheinigung</SelectItem>
                                            </SelectGroup>
                                            <SelectGroup>
                                                <SelectLabel>Belege</SelectLabel>
                                                <SelectItem value="payment_receipt">Zahlungsbestätigung</SelectItem>
                                                <SelectItem value="in_house_invoice">In-House-Rechnung (Firmen-Sammelrechnung)</SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {!selectedTemplate && (
                                    <div className="space-y-2">
                                        <Label>Format</Label>
                                        <Select value={formData.orientation} onValueChange={(v) => setFormData({ ...formData, orientation: v })}>
                                            <SelectTrigger data-testid="template-orientation-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="a4-portrait">A4 Hochformat</SelectItem>
                                                <SelectItem value="a4-landscape">A4 Querformat</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            {!selectedTemplate && (
                                <div className="space-y-2">
                                    <Label>Hintergrundbild (optional)</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Laden Sie Ihre bestehende Vorlage als PNG oder JPG hoch. Das Bild wird seitengroß als fixierter Hintergrund eingesetzt.
                                    </p>
                                    {bgPreview ? (
                                        <div className="relative border rounded-md overflow-hidden bg-muted p-2">
                                            <img src={bgPreview} alt="Vorschau" className="max-h-40 mx-auto rounded" />
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs text-muted-foreground truncate">{bgFile?.name}</span>
                                                <Button
                                                    type="button" variant="ghost" size="sm" className="text-red-600 text-xs h-7"
                                                    onClick={() => { setBgFile(null); setBgPreview(null); if (bgFileRef.current) bgFileRef.current.value = ''; }}
                                                    data-testid="remove-quickstart-bg"
                                                >
                                                    <Trash2 className="h-3 w-3 mr-1" /> Entfernen
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <Button
                                            type="button" variant="outline" className="w-full h-20 border-dashed"
                                            onClick={() => bgFileRef.current?.click()}
                                            data-testid="quickstart-bg-upload"
                                        >
                                            <div className="flex flex-col items-center gap-1 text-muted-foreground">
                                                <ImageIcon className="h-6 w-6" />
                                                <span className="text-xs">PNG oder JPG hochladen</span>
                                            </div>
                                        </Button>
                                    )}
                                    <input
                                        ref={bgFileRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) {
                                                setBgFile(f);
                                                setBgPreview(URL.createObjectURL(f));
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                            <Button type="submit" disabled={saving} data-testid="save-template-button">
                                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {selectedTemplate ? 'Speichern' : (bgFile ? 'Erstellen mit Hintergrund' : 'Erstellen & Gestalten')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Vorlage löschen?</DialogTitle>
                        <DialogDescription>Diese Aktion kann nicht rückgängig gemacht werden.</DialogDescription>
                    </DialogHeader>
                    <p className="text-muted-foreground">
                        Möchten Sie <strong>"{selectedTemplate?.name}"</strong> wirklich löschen?
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                        <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-template">Löschen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </AdminLayout>
    );
}
